/**
 * Geometría de zoom, desplazamiento, localización de butacas y navegación con flechas. Sin DOM.
 *
 * Convenciones:
 *  - "mundo" = coordenadas del plano; "pantalla" = píxeles dentro del visor.
 *  - Una vista es `{k, cx, cy}`: zoom relativo al encaje (k=1 muestra todo el plano) y el punto del
 *    mundo que queda en el centro del visor. Así sobrevive a cambios de tamaño del contenedor.
 */

export interface View {
  k: number;
  cx: number;
  cy: number;
}
export interface Frame {
  /** tamaño del visor en px */
  w: number;
  h: number;
  /** tamaño del plano */
  ww: number;
  wh: number;
  /** escala (px por unidad) con k = 1 */
  fit: number;
  /** zoom máximo (multiplicador sobre fit) */
  kMax: number;
}

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/**
 * `pitch` es la separación típica entre butacas: el zoom máximo deja las butacas a ~56 px entre sí
 * (cómodo para el pulgar) y nunca menos de 4x sobre el encaje.
 */
export function makeFrame(w: number, h: number, ww: number, wh: number, pitch: number): Frame {
  const fit = Math.max(1e-6, Math.min(w / ww, h / wh));
  const maxScale = Math.max(fit * 4, 56 / Math.max(1, pitch));
  return { w, h, ww, wh, fit, kMax: Math.max(1.5, maxScale / fit) };
}

export const scaleOf = (f: Frame, v: View): number => f.fit * v.k;

export function clampView(f: Frame, v: View): View {
  const k = clamp(v.k, 1, f.kMax);
  const s = f.fit * k;
  const hx = f.w / (2 * s);
  const hy = f.h / (2 * s);
  const cx = f.ww * s <= f.w ? f.ww / 2 : clamp(v.cx, hx, f.ww - hx);
  const cy = f.wh * s <= f.h ? f.wh / 2 : clamp(v.cy, hy, f.wh - hy);
  return { k, cx, cy };
}

export function toWorld(f: Frame, v: View, px: number, py: number): { x: number; y: number } {
  const s = scaleOf(f, v);
  return { x: v.cx + (px - f.w / 2) / s, y: v.cy + (py - f.h / 2) / s };
}

export function toScreen(f: Frame, v: View, x: number, y: number): { x: number; y: number } {
  const s = scaleOf(f, v);
  return { x: f.w / 2 + (x - v.cx) * s, y: f.h / 2 + (y - v.cy) * s };
}

/** Transformación CSS/SVG equivalente de la vista: screen = world * s + t */
export function transformOf(f: Frame, v: View): { s: number; tx: number; ty: number } {
  const s = scaleOf(f, v);
  return { s, tx: f.w / 2 - v.cx * s, ty: f.h / 2 - v.cy * s };
}

/** Cambia el zoom manteniendo fijo el punto del mundo que está bajo (px, py). */
export function zoomAt(f: Frame, v: View, px: number, py: number, newK: number): View {
  const k = clamp(newK, 1, f.kMax);
  const w0 = toWorld(f, v, px, py);
  const s = f.fit * k;
  return clampView(f, { k, cx: w0.x - (px - f.w / 2) / s, cy: w0.y - (py - f.h / 2) / s });
}

/** Desplaza la vista `dx, dy` píxeles de pantalla (arrastrar). */
export function panBy(f: Frame, v: View, dx: number, dy: number): View {
  const s = scaleOf(f, v);
  return clampView(f, { k: v.k, cx: v.cx - dx / s, cy: v.cy - dy / s });
}

export interface Pt {
  x: number;
  y: number;
}

/**
 * Pellizco: de los dos dedos iniciales (a0, b0) a los actuales (a1, b1). El zoom sigue la razón de
 * distancias y el punto medio inicial acaba bajo el punto medio actual (zoom + desplazamiento a la vez).
 */
export function pinchView(f: Frame, start: View, a0: Pt, b0: Pt, a1: Pt, b1: Pt): View {
  const d0 = Math.max(1, Math.hypot(a0.x - b0.x, a0.y - b0.y));
  const d1 = Math.max(1, Math.hypot(a1.x - b1.x, a1.y - b1.y));
  const k = clamp(start.k * (d1 / d0), 1, f.kMax);
  const m0 = { x: (a0.x + b0.x) / 2, y: (a0.y + b0.y) / 2 };
  const m1 = { x: (a1.x + b1.x) / 2, y: (a1.y + b1.y) / 2 };
  const w0 = toWorld(f, start, m0.x, m0.y);
  const s = f.fit * k;
  return clampView(f, { k, cx: w0.x - (m1.x - f.w / 2) / s, cy: w0.y - (m1.y - f.h / 2) / s });
}

/** Vista con zoom `k` centrada en un punto del mundo. */
export function centerOn(f: Frame, x: number, y: number, k: number): View {
  return clampView(f, { k, cx: x, cy: y });
}

/** Factor de zoom para la rueda: suave y simétrico (acercar y alejar se compensan). */
export const wheelFactor = (deltaY: number, deltaMode = 0): number => {
  const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 100 : deltaY;
  return Math.exp(-clamp(px, -120, 120) * 0.0022);
};

/** k que deja las butacas separadas `targetPx` píxeles en pantalla. */
export const kForPitch = (f: Frame, pitch: number, targetPx: number): number => clamp(targetPx / (pitch * f.fit), 1, f.kMax);

/* ------------------------------------------------------------------------ */
/* Índice espacial para localizar la butaca más cercana a un punto           */
/* ------------------------------------------------------------------------ */

export class Grid {
  private cells = new Map<number, number[]>();
  constructor(
    private readonly xs: ArrayLike<number>,
    private readonly ys: ArrayLike<number>,
    private readonly cell: number,
  ) {
    for (let i = 0; i < xs.length; i++) {
      const key = this.key(Math.floor(xs[i]! / cell), Math.floor(ys[i]! / cell));
      const list = this.cells.get(key);
      if (list) list.push(i);
      else this.cells.set(key, [i]);
    }
  }
  private key(cx: number, cy: number): number {
    return (cx + 32768) * 65536 + (cy + 32768);
  }
  /** Índice del punto más cercano a (x, y) a distancia <= maxD, o -1. */
  nearest(x: number, y: number, maxD: number): number {
    const c = this.cell;
    const r = Math.ceil(maxD / c);
    const cx = Math.floor(x / c);
    const cy = Math.floor(y / c);
    let best = -1;
    let bestD = maxD * maxD;
    for (let gx = cx - r; gx <= cx + r; gx++) {
      for (let gy = cy - r; gy <= cy + r; gy++) {
        const list = this.cells.get(this.key(gx, gy));
        if (!list) continue;
        for (const i of list) {
          const dx = this.xs[i]! - x;
          const dy = this.ys[i]! - y;
          const d = dx * dx + dy * dy;
          if (d <= bestD) {
            bestD = d;
            best = i;
          }
        }
      }
    }
    return best;
  }
}

/* ------------------------------------------------------------------------ */
/* Navegación con flechas siguiendo la geometría                             */
/* ------------------------------------------------------------------------ */

export type Dir = "left" | "right" | "up" | "down";

export interface NavData {
  xs: ArrayLike<number>;
  ys: ArrayLike<number>;
  /** fila de cada nodo (-1 si no pertenece a ninguna, p. ej. una zona) */
  rowOf: ArrayLike<number>;
  /** posición dentro de la fila */
  posOf: ArrayLike<number>;
  /** nodos de cada fila, en orden */
  rowNodes: number[][];
}

const VEC: Record<Dir, [number, number]> = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] };

/**
 * Siguiente nodo en una dirección. Primero se intenta la butaca contigua de la misma fila si queda en esa
 * dirección (así izquierda/derecha recorren la fila aunque sea un arco); si no, el nodo más cercano dentro
 * de un cono hacia esa dirección. Devuelve -1 si no hay nada.
 */
export function navigate(nav: NavData, from: number, dir: Dir): number {
  const dx = VEC[dir][0];
  const dy = VEC[dir][1];
  const fx = nav.xs[from]!;
  const fy = nav.ys[from]!;

  const row = nav.rowOf[from]!;
  if (row >= 0) {
    const nodes = nav.rowNodes[row]!;
    const pos = nav.posOf[from]!;
    let best = -1;
    let bestAlong = 0;
    for (const p of [pos - 1, pos + 1]) {
      const j = nodes[p];
      if (j === undefined) continue;
      const vx = nav.xs[j]! - fx;
      const vy = nav.ys[j]! - fy;
      const along = vx * dx + vy * dy;
      const lateral = Math.abs(vx * dy - vy * dx);
      if (along > 0 && along >= lateral * 0.7 && (best < 0 || along < bestAlong)) {
        best = j;
        bestAlong = along;
      }
    }
    if (best >= 0) return best;
  }

  for (const cone of [1, 3]) {
    let best = -1;
    let bestScore = Infinity;
    for (let j = 0; j < nav.xs.length; j++) {
      if (j === from) continue;
      const vx = nav.xs[j]! - fx;
      const vy = nav.ys[j]! - fy;
      const along = vx * dx + vy * dy;
      if (along <= 0.001) continue;
      const lateral = Math.abs(vx * dy - vy * dx);
      if (lateral > along * cone) continue;
      const score = along + lateral * 2.2;
      if (score < bestScore) {
        bestScore = score;
        best = j;
      }
    }
    if (best >= 0) return best;
  }
  return -1;
}
